import React, { useEffect, useId, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";

interface IconTooltipProps {
  icon: LucideIcon;
  text: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  iconClassName?: string;
}

const tooltipPositionClasses: Record<
  NonNullable<IconTooltipProps["position"]>,
  string
> = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

const arrowPositionClasses: Record<
  NonNullable<IconTooltipProps["position"]>,
  string
> = {
  top: "top-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-800",
  bottom:
    "bottom-full left-1/2 -translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-800",
  left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-800",
  right:
    "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-800",
};

export function IconTooltip({
  icon: Icon,
  text,
  position = "top",
  iconClassName = "",
}: IconTooltipProps) {
  const [visible, setVisible] = useState(false);
  const tooltipId = useId();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible]);

  return (
    <button
      ref={ref}
      type="button"
      className="relative inline-flex cursor-help items-center focus:outline-none"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onClick={() => setVisible((v) => !v)}
      aria-describedby={visible ? tooltipId : undefined}
    >
      <Icon className={`${iconClassName}`} />

      {visible && (
        <div
          id={tooltipId}
          role="tooltip"
          className={`absolute z-50 ${tooltipPositionClasses[position]}`}
        >
          <div className="w-max max-w-55 rounded-lg bg-gray-800 px-3 py-2 text-xs leading-snug text-white shadow-lg">
            {text}
          </div>
          <div
            className={`absolute h-0 w-0 border-4 ${arrowPositionClasses[position]}`}
          />
        </div>
      )}
    </button>
  );
}
